import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { LoadingButton } from "@mui/lab";
import {
  enhanceUserMemory,
  getUserMemory,
  saveUserMemory,
  UserMemory,
} from "api";
import { useSelectedOrganization, useTranslations } from "hooks";
import { getTranslation } from "common";

/**
 * Inline section on the Profile page: the free-text "AI memory" the backend
 * injects into every LLM request this user makes (guide drawer, BPMN
 * modeller, robot builder, AI Assistant).
 *
 * Two things the UI must make obvious, because they are not obvious at all:
 *  - the text is read by the AI in EVERY conversation (hence the description);
 *  - every character is paid for on every request (hence the counter, which
 *    warns past the suggested length well before the hard cap).
 *
 * "Migliora con l'AI" never writes: it fills `proposal`, the user reads the
 * rewrite next to their own words and either applies it (which only fills the
 * editor — Save still commits) or discards it.
 */

/** Suggested length. The hard cap comes from the backend (`max_chars`). */
const SUGGESTED_CHARS = 1500;
const FALLBACK_MAX_CHARS = 4000;

const AiMemorySection: React.FC = () => {
  const translations = useTranslations();
  const organization = useSelectedOrganization();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [saved, setSaved] = useState<UserMemory | null>(null);
  const [memory, setMemory] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [proposal, setProposal] = useState<string | null>(null);

  const t = (key: string, fallback: string) =>
    getTranslation(translations, key) || fallback;

  const maxChars = saved?.max_chars || FALLBACK_MAX_CHARS;

  const readError = (e: any, fallback: string) =>
    e?.response?.data?.detail || e?.message || fallback;

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserMemory();
      setSaved(data);
      setMemory(data.memory);
      setEnabled(data.enabled);
    } catch (e: any) {
      setError(readError(e, "Impossibile caricare la memoria AI."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const dirty = useMemo(() => {
    if (!saved) return memory.trim().length > 0;
    return memory.trim() !== saved.memory.trim() || enabled !== saved.enabled;
  }, [memory, enabled, saved]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const data = await saveUserMemory(memory.trim(), enabled);
      setSaved(data);
      setMemory(data.memory);
      setEnabled(data.enabled);
      setProposal(null);
      setNotice(t("profile.ai_memory.saved", "Memoria salvata."));
    } catch (e: any) {
      setError(readError(e, "Impossibile salvare la memoria AI."));
    } finally {
      setSaving(false);
    }
  };

  const handleEnhance = async () => {
    const trimmed = memory.trim();
    if (!trimmed) return;
    setEnhancing(true);
    setError(null);
    setNotice(null);
    try {
      const data = await enhanceUserMemory(trimmed, organization?.id);
      setProposal(data.enhanced);
    } catch (e: any) {
      setError(
        readError(e, "Il modello non ha risposto. Riprova tra un momento.")
      );
    } finally {
      setEnhancing(false);
    }
  };

  const applyProposal = () => {
    if (proposal === null) return;
    // Fills the editor only: the user still has to press Save, so a rewrite
    // they dislike costs them nothing.
    setMemory(proposal);
    setProposal(null);
    setNotice(
      t(
        "profile.ai_memory.enhance_applied",
        "Testo migliorato applicato: premi Salva per confermarlo."
      )
    );
  };

  const counterColor = memory.length > SUGGESTED_CHARS ? "warning.main" : "text.secondary";

  return (
    <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        {t("profile.ai_memory.title", "Memoria AI")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          "profile.ai_memory.description",
          "Scrivi qui chi sei e come lavori: ruolo, organizzazioni che segui, come chiami le cose, in che lingua preferisci le risposte. L'assistente AI legge questo testo all'inizio di ogni conversazione, in tutte le sezioni di Flowenti."
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rounded" height={160} />
      ) : (
        <>
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={14}
            value={memory}
            onChange={(e) => setMemory(e.target.value.slice(0, maxChars))}
            placeholder={t(
              "profile.ai_memory.placeholder",
              "Es: Sono il responsabile acquisti di Acme. Seguo le organizzazioni Acme Italia e Acme Iberia. Chiamiamo 'pratica' quello che in Flowenti è un processo. Rispondimi in italiano, con risposte brevi."
            )}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ mt: 1 }}
          >
            <Typography variant="caption" sx={{ color: counterColor }}>
              {memory.length} / {maxChars}
              {memory.length > SUGGESTED_CHARS
                ? ` — ${t(
                    "profile.ai_memory.too_long",
                    `oltre ${SUGGESTED_CHARS} caratteri consigliati: il testo viene inviato ad ogni richiesta`
                  )}`
                : ""}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label={t(
                "profile.ai_memory.enabled",
                "Usa la memoria nelle chat AI"
              )}
            />
          </Stack>

          {proposal !== null && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("profile.ai_memory.proposal_title", "Proposta dell'AI")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", color: "text.secondary", mb: 2 }}
              >
                {proposal}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" onClick={applyProposal}>
                  {t("profile.ai_memory.apply", "Applica")}
                </Button>
                <Button size="small" onClick={() => setProposal(null)}>
                  {t("profile.ai_memory.discard", "Annulla")}
                </Button>
              </Stack>
            </Paper>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <LoadingButton
              variant="contained"
              loading={saving}
              disabled={!dirty}
              onClick={handleSave}
            >
              {t("profile.ai_memory.save", "Salva")}
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              loading={enhancing}
              disabled={!memory.trim()}
              startIcon={<AutoAwesomeIcon />}
              onClick={handleEnhance}
            >
              {t("profile.ai_memory.enhance", "Migliora con l'AI")}
            </LoadingButton>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default AiMemorySection;
